<template>
    <div class="page-container">
        <div class="container-fluid">
            <!-- Navigation Breadcrumb -->
            <div class="page-navigation">
                <div class="breadcrumb-custom">
                    <router-link to="/">
                        <i class="fas fa-home"></i> Home
                    </router-link>
                    <span class="separator">/</span>
                    <span>Hasil Tamhidi</span>
                </div>
            </div>

            <!-- Page Header -->
            <div class="page-header">
                <h2 class="page-title">Hasil Tamhidi / Mustawa</h2>
                <p class="page-subtitle">Rekod nilai program Tamhidi dan Mustawa</p>
            </div>

            <div class="row justify-content-center">
                <div class="col-12 col-lg-6">

                    <!-- Type 4: Bahasa (Melayu/Arab) Mustawa Summary — cohorts 2022+ -->
                    <div v-if="applyYear == '4' && summary" class="bahasa-summary">

                        <!-- Not compulsory for this cohort (income_year < 2022) -->
                        <div v-if="summary.not_compulsory" class="eligibility-banner info">
                            <i class="fas fa-info-circle"></i>
                            <span>{{ summary.summary_message }}</span>
                        </div>

                        <template v-else>
                            <!-- Eligibility Banner -->
                            <div class="eligibility-banner" :class="summary.is_eligible ? 'success' : 'warning'">
                                <i :class="summary.is_eligible ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
                                <span>{{ summary.summary_message }}</span>
                            </div>

                            <!-- Melayu -->
                            <div class="collapsible-card">
                                <div class="lang-header"><i class="fas fa-language"></i> Bahasa Melayu</div>
                                <div class="lang-row">
                                    <span class="lang-level">Status</span>
                                    <span class="score-badge" :class="getBahasaClass(getOverallLabel(summary.melayu_level_one_label, summary.melayu_level_two_label))">{{ getOverallLabel(summary.melayu_level_one_label, summary.melayu_level_two_label) }}</span>
                                </div>
                            </div>

                            <!-- Arab -->
                            <div class="collapsible-card mt-2">
                                <div class="lang-header"><i class="fas fa-language"></i> Bahasa Arab</div>
                                <div class="lang-row">
                                    <span class="lang-level">Status</span>
                                    <span class="score-badge" :class="getBahasaClass(getOverallLabel(summary.arabic_level_one_label, summary.arabic_level_two_label))">{{ getOverallLabel(summary.arabic_level_one_label, summary.arabic_level_two_label) }}</span>
                                </div>
                            </div>

                            <!-- Not yet finalized note -->
                            <p v-if="!summary.generated" class="text-muted pending-note">
                                <i class="fas fa-info-circle"></i>
                                Natijah belum disahkan rasmi. Sila hubungi pentadbir jika terdapat sebarang pertanyaan.
                            </p>
                        </template>
                    </div>

                    <!-- Results List (legacy cohorts: type 1/2/3) -->
                    <template v-else><!-- Results List -->
                    <div class="collapsible-list">
                        <div class="collapsible-card" v-for="(item, index) in items" :key="index">
                            <!-- Semester Header -->
                            <div class="collapsible-header" 
                                 :class="{ 'collapsed': !item.isOpen }"
                                 @click="toggleItem(item)">
                                <div class="collapsible-title">
                                    <i class="fas fa-book-open"></i>
                                    <span v-if="applyYear == '1' || applyYear == '2'">
                                        Semester {{ item.semester }} / {{ item.year }}
                                    </span>
                                    <span v-else>
                                        {{ item.subject }} Tahap {{ item.level }}
                                    </span>
                                </div>
                                <div class="collapsible-meta">
                                    <span class="collapsible-count" v-if="applyYear == '3'">
                                        {{ item.semester }}/{{ item.year }}
                                    </span>
                                    <i class="fas fa-chevron-down collapsible-toggle"></i>
                                </div>
                            </div>
                            
                            <!-- Result Body (Collapsible) -->
                            <div class="collapsible-body" v-show="item.isOpen">
                                <!-- Type 1: Tamhidi -->
                                <div class="list-item" v-if="applyYear == '1'">
                                    <div class="list-item-header">
                                        <span class="list-item-number">
                                            <i class="fas fa-graduation-cap"></i>
                                        </span>
                                        <div class="list-item-info">
                                            <span class="list-item-name">Tamhidi {{ item.level }}</span>
                                        </div>
                                    </div>
                                    <div class="list-item-details">
                                        <div class="detail-row">
                                            <i class="fas fa-star"></i>
                                            <span class="score-badge" :class="getScoreClass(item.score)">
                                                Markah: {{ item.score || '-' }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Type 2: Mustawa -->
                                <div class="list-item" v-if="applyYear == '2'">
                                    <div class="list-item-header">
                                        <span class="list-item-number">
                                            <i class="fas fa-graduation-cap"></i>
                                        </span>
                                        <div class="list-item-info">
                                            <span class="list-item-name">Mustawa {{ item.level }}</span>
                                        </div>
                                    </div>
                                    <div class="list-item-details">
                                        <div class="detail-row">
                                            <i class="fas fa-check-circle"></i>
                                            <span class="score-badge success" v-if="item.score == 1">Lulus</span>
                                            <span class="score-badge danger" v-else-if="item.score == 2">Tidak Lulus</span>
                                            <span class="score-badge warning" v-else>Belum Ada Natijah</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Type 3: New Format -->
                                <div class="list-item" v-if="applyYear == '3'">
                                    <div class="list-item-details" style="margin-left: 0;">
                                        <div class="detail-row">
                                            <i class="fas fa-check-circle"></i>
                                            <span>Natijah:</span>
                                            <span class="score-badge" :class="getResultClass(item.score)">
                                                {{ item.score }}
                                            </span>
                                            <span v-if="item.score == 'Tidak lengkap'" class="text-muted">
                                                (Sila hubungi pengajar)
                                            </span>
                                        </div>
                                        <div class="detail-row" v-if="item.teacher_name">
                                            <i class="fas fa-user-tie"></i>
                                            <span>Pengajar: {{ item.teacher_name }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Empty State -->
                        <div v-if="items.length === 0" class="empty-state">
                            <i class="fas fa-graduation-cap"></i>
                            <p>Tiada hasil Tamhidi dijumpai</p>
                        </div>
                    </div>
                    </template>

                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import http from '@/services/BackendService'
    export default {
        mounted(){
            this.retreiveTamhidiResult()
        },
        data(){
            return{
                items: [],
                summary: null,
                applyYear: '',
            }
        },
        methods:{
            toggleItem(item) {
                this.$set(item, 'isOpen', !item.isOpen)
            },
            getBahasaClass(label){
                if(label && label.indexOf('Lulus') === 0){
                    return 'success'
                }
                return 'warning'
            },
            getOverallLabel(levelOneLabel, levelTwoLabel){
                const passed = (levelOneLabel && levelOneLabel.indexOf('Lulus') === 0)
                    || (levelTwoLabel && levelTwoLabel.indexOf('Lulus') === 0)
                return passed ? 'Lulus' : 'Belum Lulus'
            },
            getResultClass(score){
                if(score == 'Lulus'){
                    return 'success'
                }else if(score == 'Gagal'){
                    return 'danger'
                }else if(score == 'Tidak hadir periksa' || score == 'Tidak layak periksa'){
                    return 'warning'
                }else if(score == 'Tidak lengkap'){
                    return 'info'
                }else{
                    return 'secondary'
                }
            },
            getScoreClass(score) {
                if (score == '' || score == null) {
                    return 'warning'
                } else if (score < 50) {
                    return 'danger'
                } else {
                    return 'success'
                }
            },
            retreiveTamhidiResult(){
                http.get('tamhidi/result')
                    .then(response => {
                        this.applyYear = response.data.type
                        if (response.data.type == '4') {
                            this.summary = response.data.result
                            this.items = []
                        } else {
                            this.summary = null
                            this.items = (response.data.result || []).map(item => ({
                                ...item,
                                isOpen: false
                            }))
                        }
                    })
                    .catch(error => {
                        console.log(error)
                    })
            }
        }
    }
</script>

<style scoped>
/* Additional badge colors not in theme.css */
.score-badge.info {
    background: rgba(23, 162, 184, 0.15);
    color: #117a8b;
}

.score-badge.secondary {
    background: rgba(108, 117, 125, 0.15);
    color: #6c757d;
}

.text-muted {
    color: var(--text-secondary);
    font-size: 0.85rem;
    font-style: italic;
}

/* Type 4: Bahasa mustawa summary */
.eligibility-banner {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.85rem 1rem;
    border-radius: 8px;
    margin-bottom: 1rem;
    font-weight: 500;
}

.eligibility-banner.success {
    background: rgba(40, 167, 69, 0.12);
    color: #1e7e34;
}

.eligibility-banner.warning {
    background: rgba(255, 193, 7, 0.15);
    color: #b8860b;
}

.eligibility-banner.info {
    background: rgba(23, 162, 184, 0.15);
    color: #117a8b;
}

.lang-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--border-color, #eee);
}

.lang-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.65rem 1rem;
}

.lang-level {
    color: var(--text-secondary);
}

.pending-note {
    margin-top: 0.75rem;
}
</style>